/**
 * TutorialOverlay — anchored, multi-step guided tour.
 *
 * Each tour is a list of steps:
 *   { selector: '[data-testid="..."]', title, body, placement: 'bottom'|'top'|'left'|'right'|'center', spotlight: bool }
 *
 * Behaviour:
 *   - Dimmed full-screen backdrop with a transparent "spotlight" cutout
 *     around the target element (via CSS box-shadow inversion).
 *   - Tooltip card positioned near the spotlight (clamped to viewport).
 *   - If selector misses, gracefully centers (placement='center').
 *   - Keyboard: Esc closes, → / Enter advances, ← rewinds.
 *   - Completion persisted to localStorage under key `resmo:tutorial:<id>:done`.
 *
 * Usage:
 *   <TutorialOverlay
 *     id="dashboard"
 *     open={open}
 *     steps={DASHBOARD_TUTORIAL}
 *     onClose={() => setOpen(false)}
 *   />
 */
import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { ChevronRight, ChevronLeft, X } from "lucide-react";

const PADDING = 8;          // px around spotlight cutout
const TOOLTIP_GAP = 14;     // px between spotlight and tooltip
const TOOLTIP_W = 380;

function getRect(selector) {
  if (!selector || typeof document === "undefined") return null;
  const el = document.querySelector(selector);
  if (!el || !el.getBoundingClientRect) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height, el };
}

function scrollIntoView(selector) {
  const el = selector && typeof document !== "undefined" ? document.querySelector(selector) : null;
  if (el && el.scrollIntoView) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

export function tutorialDone(id) {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem(`resmo:tutorial:${id}:done`) === "1"; }
  catch { return false; }
}

export function markTutorialDone(id) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(`resmo:tutorial:${id}:done`, "1"); } catch { /* ignore */ }
}

export default function TutorialOverlay({ id, open, steps, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [, force] = useState(0);
  const lastFocus = useRef(null);

  const step = steps?.[stepIdx];

  // Reset to step 0 every time the overlay opens.
  useEffect(() => {
    if (open) {
      setStepIdx(0);
      setDontAskAgain(false);
    }
  }, [open]);

  // Scroll the current step's anchor into view, then recompute rect.
  useEffect(() => {
    if (!open || !step) return undefined;
    if (step.selector) scrollIntoView(step.selector);
    const tick = () => {
      const r = step.selector ? getRect(step.selector) : null;
      setRect(r);
      force((n) => n + 1);
    };
    // Allow scroll-into-view to finish before measuring.
    const t1 = setTimeout(tick, 250);
    const t2 = setTimeout(tick, 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [open, stepIdx, step]);

  // Re-measure on window resize/scroll while open.
  useLayoutEffect(() => {
    if (!open || !step?.selector) return undefined;
    const tick = () => setRect(getRect(step.selector));
    window.addEventListener("resize", tick);
    window.addEventListener("scroll", tick, true);
    return () => {
      window.removeEventListener("resize", tick);
      window.removeEventListener("scroll", tick, true);
    };
  }, [open, step]);

  const finish = useCallback((skipped = false) => {
    if (!skipped) markTutorialDone(id);
    onClose && onClose({ skipped, dontAskAgain });
  }, [id, onClose, dontAskAgain]);

  const next = useCallback(() => {
    if (stepIdx >= (steps?.length || 0) - 1) finish(false);
    else setStepIdx((i) => i + 1);
  }, [stepIdx, steps, finish]);

  const prev = useCallback(() => {
    setStepIdx((i) => Math.max(0, i - 1));
  }, []);

  // Keyboard nav.
  useEffect(() => {
    if (!open) return undefined;
    lastFocus.current = document.activeElement;
    const onKey = (e) => {
      if (e.key === "Escape") finish(true);
      else if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (lastFocus.current?.focus) lastFocus.current.focus();
    };
  }, [open, next, prev, finish]);

  if (!open || !step) return null;

  const placement = step.placement || (rect ? "auto" : "center");
  const hasSpotlight = !!rect && step.spotlight !== false;

  // Compute spotlight cutout box.
  let spotBox = null;
  if (hasSpotlight) {
    spotBox = {
      top: rect.top - PADDING,
      left: rect.left - PADDING,
      width: rect.width + PADDING * 2,
      height: rect.height + PADDING * 2,
    };
  }

  // Compute tooltip position.
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  let tip = { top: vh / 2 - 120, left: Math.max(16, vw / 2 - TOOLTIP_W / 2) };
  if (hasSpotlight && placement !== "center") {
    const r = spotBox;
    const auto = placement === "auto";
    // Prefer below if room, else above; clamp left.
    const below = r.top + r.height + TOOLTIP_GAP;
    const above = r.top - TOOLTIP_GAP - 200;
    let useTop = placement === "top";
    if (auto) useTop = below + 220 > vh && r.top > 240;
    tip = {
      top: useTop ? Math.max(16, above) : Math.min(vh - 240, below),
      left: Math.max(16, Math.min(vw - TOOLTIP_W - 16, r.left + r.width / 2 - TOOLTIP_W / 2)),
    };
    if (placement === "left")  tip = { top: Math.max(16, r.top), left: Math.max(16, r.left - TOOLTIP_W - TOOLTIP_GAP) };
    if (placement === "right") tip = { top: Math.max(16, r.top), left: Math.min(vw - TOOLTIP_W - 16, r.left + r.width + TOOLTIP_GAP) };
  }

  const total = steps.length;
  const isLast = stepIdx === total - 1;

  return (
    <div
      className="fixed inset-0"
      style={{ zIndex: 100 }}
      data-testid={`tutorial-${id}`}
    >
      {/* Backdrop with spotlight cutout */}
      {hasSpotlight ? (
        <div
          className="absolute inset-0"
          style={{
            // Massive outer shadow effectively darkens everything outside.
            boxShadow: `0 0 0 9999px rgba(15,27,45,0.62)`,
            top: spotBox.top,
            left: spotBox.left,
            width: spotBox.width,
            height: spotBox.height,
            borderRadius: 10,
            border: "2px solid #FFC145",
            pointerEvents: "none",
            transition: "all 220ms ease-out",
          }}
          data-testid={`tutorial-${id}-spotlight`}
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: "rgba(15,27,45,0.62)" }}
          onClick={() => finish(true)}
          data-testid={`tutorial-${id}-backdrop`}
        />
      )}

      {/* Tooltip card */}
      <div
        className="absolute"
        style={{
          top: tip.top,
          left: tip.left,
          width: TOOLTIP_W,
          maxWidth: "calc(100vw - 32px)",
          background: "#FFF2EC",
          border: "3px solid #0F1B2D",
          borderRadius: 12,
          boxShadow: "8px 8px 0 0 #0F1B2D",
          transition: "top 220ms ease-out, left 220ms ease-out",
          zIndex: 2,
        }}
        role="dialog"
        aria-modal="true"
        data-testid={`tutorial-${id}-tooltip`}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-2">
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                 style={{ color: "#94A3B8", fontWeight: 700 }}>
              · Resmo tour · step {stepIdx + 1} of {total}
            </div>
            <div className="font-display text-xl uppercase leading-tight mt-1"
                 style={{ color: step.accentColor || "#FF6A00" }}>
              {step.title}
            </div>
          </div>
          <button
            type="button"
            onClick={() => finish(true)}
            className="rm-btn rm-btn-ghost text-xs px-2 py-1.5"
            data-testid={`tutorial-${id}-close`}
            aria-label="Close tour"
          >
            <X size={13} />
          </button>
        </div>

        <div className="px-5 pb-3 text-[13px] leading-relaxed" style={{ color: "#0F1B2D" }}
             data-testid={`tutorial-${id}-body`}>
          {step.body}
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 mb-2" data-testid={`tutorial-${id}-dots`}>
          {steps.map((_, i) => (
            <div
              key={i}
              className="rounded-full"
              style={{
                width: i === stepIdx ? 18 : 6,
                height: 6,
                background: i === stepIdx ? "#FF6A00" : "#CBD5E1",
                transition: "width 200ms",
              }}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-1">
          <button
            type="button"
            onClick={prev}
            disabled={stepIdx === 0}
            className="rm-btn rm-btn-ghost text-xs px-2 py-1.5"
            style={{ opacity: stepIdx === 0 ? 0.4 : 1 }}
            data-testid={`tutorial-${id}-prev`}
          >
            <ChevronLeft size={13} /> Back
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => finish(true)}
              className="font-mono-rm text-[10px] uppercase tracking-widest underline"
              style={{ color: "#64748B" }}
              data-testid={`tutorial-${id}-skip`}
            >
              Skip
            </button>
            <button
              type="button"
              onClick={next}
              className="rm-btn rm-btn-primary text-xs px-3 py-1.5"
              data-testid={`tutorial-${id}-next`}
            >
              {isLast ? "Got it" : "Next"} <ChevronRight size={13} />
            </button>
          </div>
        </div>
        {/* Don't-ask-again opt-out — applies globally across all tours */}
        <label
          className="flex items-center gap-2 px-4 pb-3 cursor-pointer select-none"
          data-testid={`tutorial-${id}-dont-ask-label`}
        >
          <input
            type="checkbox"
            checked={dontAskAgain}
            onChange={(e) => setDontAskAgain(e.target.checked)}
            className="rm-checkbox"
            style={{ accentColor: "#FF6A00" }}
            data-testid={`tutorial-${id}-dont-ask`}
          />
          <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                style={{ color: "#64748B" }}>
            Don't show Resmo tours next time
          </span>
        </label>
      </div>
    </div>
  );
}
